import React from 'react';
import { Edit, Trash2, Package, Search, Eye, EyeOff } from 'lucide-react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { getGlassStyles } from './ProductManagementStyles';
import MultiSelectDropdown from '../../../../core/ui/MultiSelectDropdown';
import MoreFiltersPicker from '../../../../core/ui/MoreFiltersPicker';
import useVisibleFilters from '../../../../core/ui/useVisibleFilters';

const ALL_FILTER_KEYS = ['category', 'status', 'manufacturer', 'source'];
const DEFAULT_FILTER_KEYS = ['category', 'status'];

const ProductList = ({
  products,
  user,
  loading,
  filteredProducts,
  searchTerm,
  setSearchTerm,
  pFilters,
  setPFilters,
  filterOptions,
  onEditProduct,
  onDeleteProduct
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const styles = getGlassStyles(isDarkMode);
  const [visibleFilterKeys, setVisibleFilterKeys] = useVisibleFilters(user, 'product-filter-fields', DEFAULT_FILTER_KEYS, ALL_FILTER_KEYS);

  const renderFilters = () => {
    const setF = (k) => (next) => setPFilters((f) => ({ ...f, [k]: next }));
    const hasFilter = Object.values(pFilters).some((a) => a.length > 0);
    const bulk = { selectAllLabel: t('kpi.selectAll'), clearAllLabel: t('kpi.clearAll'), searchPlaceholder: t('kpi.search') };
    const statusOpts = [
      { value: 'active', label: t('admin.products.statusActive') },
      { value: 'inactive', label: t('admin.products.statusInactive') },
    ];
    const sourceOpts = (filterOptions.source || []).map((s) => ({ value: s, label: s === 'sap' ? t('admin.products.sourceSap') : t('admin.products.sourceInternal') }));
    return (
      <div className="relative z-30 group mb-6">
        <div className={styles.glassBackground}></div>
        <div className={styles.glassOverlay}></div>

        <div className="relative p-6 space-y-4">
          {/* Suche */}
          <div className="relative group/search">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]' : 'bg-white border border-slate-200'} rounded-2xl`}></div>
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
              <Search className={`w-5 h-5 ${isDarkMode ? 'text-white/60 group-focus-within/search:text-blue-300/80' : 'text-slate-400 group-focus-within/search:text-brand'} transition-colors duration-300`} />
            </div>
            <input
              type="text"
              placeholder={t('admin.products.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`relative w-full pl-12 pr-4 py-3.5 bg-transparent border-0 rounded-2xl focus:ring-2 focus:outline-none transition-all duration-300 ${isDarkMode ? 'focus:ring-blue-400/50 text-white placeholder-white/60' : 'focus:ring-brand/40 text-slate-900 placeholder-slate-400'}`}
            />
          </div>

          {/* Filter-Chips — sichtbare Auswahl via „Mehr Filter" (pro User gemerkt) */}
          {(() => {
            // verfügbare Filter (nur mit sinnvollen Optionen)
            const defs = [
              { key: 'category', label: t('admin.products.filterCategory'), node: <MultiSelectDropdown label={t('admin.products.filterCategory')} options={filterOptions.category} selected={pFilters.category} onChange={setF('category')} searchable width="w-56" {...bulk} /> },
              { key: 'status', label: t('admin.products.filterStatus'), node: <MultiSelectDropdown label={t('admin.products.filterStatus')} options={statusOpts} selected={pFilters.status} onChange={setF('status')} width="w-44" {...bulk} /> },
              ...(filterOptions.manufacturer.length > 0 ? [{ key: 'manufacturer', label: t('admin.products.filterManufacturer'), node: <MultiSelectDropdown label={t('admin.products.filterManufacturer')} options={filterOptions.manufacturer} selected={pFilters.manufacturer} onChange={setF('manufacturer')} searchable width="w-52" {...bulk} /> }] : []),
              ...(sourceOpts.length > 0 ? [{ key: 'source', label: t('admin.products.filterSource'), node: <MultiSelectDropdown label={t('admin.products.filterSource')} options={sourceOpts} selected={pFilters.source} onChange={setF('source')} width="w-40" {...bulk} /> }] : []),
            ];
            // entfernte Filter zusätzlich leeren (nicht unsichtbar weiterfiltern)
            const onChangeVisible = (next) => {
              const removed = visibleFilterKeys.filter((k) => !next.includes(k));
              if (removed.length) setPFilters((f) => { const c = { ...f }; removed.forEach((k) => { c[k] = []; }); return c; });
              setVisibleFilterKeys(next);
            };
            return (
              <div className="flex flex-wrap items-center gap-2">
                {defs.filter((d) => visibleFilterKeys.includes(d.key)).map((d) => <React.Fragment key={d.key}>{d.node}</React.Fragment>)}
                <MoreFiltersPicker filters={defs.map(({ key, label }) => ({ key, label }))} visibleKeys={visibleFilterKeys} onChange={onChangeVisible} addLabel={t('kpi.moreFilters')} title={t('products.filterFields')} />
                {hasFilter && (
                  <button type="button" onClick={() => setPFilters({ category: [], status: [], manufacturer: [], source: [] })} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs ${isDarkMode ? 'text-white/60 hover:text-white/90' : 'text-slate-500 hover:text-slate-700'}`}>
                    {t('kpi.reset')} ({filteredProducts.length})
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  const renderLoading = () => (
    <div className="relative group">
      <div className={styles.glassBackground}></div>
      <div className={styles.glassOverlay}></div>
      
      <div className="relative p-16 flex justify-center items-center">
        <div className="flex flex-col items-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-3 ${isDarkMode ? 'border-white/20 border-t-blue-400' : 'border-slate-200 border-t-brand'} mb-4`}></div>
          <p className={`${isDarkMode ? 'text-white' : 'text-slate-900'} font-medium`}>{t('admin.products.loading')}</p>
          <p className={`${isDarkMode ? 'text-white/60' : 'text-slate-600'} mt-1`}>{t('common.pleaseWait')}</p>
        </div>
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div className="p-8 text-center">
      <div className="relative group/empty mb-6">
        <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-3xl blur-sm' : 'hidden'}`}></div>
        <div className={`relative w-16 h-16 rounded-3xl flex items-center justify-center mx-auto ${isDarkMode
          ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
          : 'bg-brand'
        }`}>
          <Package className={`w-8 h-8 ${isDarkMode ? 'text-white/90' : 'text-white'}`} />
          <div className={`absolute top-2 left-2 w-4 h-4 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
        </div>
      </div>
      <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'} mb-3`}>
        {products.length === 0 ? t('admin.products.empty') : t('admin.products.emptyFiltered')}
      </h3>
      <p className={`${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
        {products.length === 0
          ? t('admin.products.createFirst')
          : t('admin.products.changeFilter')
        }
      </p>
    </div>
  );

  const renderProductTableHeader = () => (
    <div className="relative group/header mb-4">
      <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-gray-900/20 via-gray-800/15 to-gray-900/20 backdrop-blur-xl border border-white/20' : 'bg-slate-50 border border-slate-200'} rounded-2xl`}></div>
      <div className={`relative p-4 grid grid-cols-6 gap-4 text-xs font-medium ${isDarkMode ? 'text-white/70' : 'text-slate-600'} uppercase tracking-wider`}>
        <div>{t('admin.products.colProduct')}</div>
        <div>{t('admin.products.colCategory')}</div>
        <div>{t('admin.products.colPrice')}</div>
        <div>{t('admin.products.colStatus')}</div>
        <div>{t('admin.products.colStock')}</div>
        <div className="text-right">{t('admin.products.colActions')}</div>
      </div>
    </div>
  );

  const renderActionButton = (onClick, icon, title, isDelete = false) => (
    <button
      onClick={onClick}
      className="relative group/action overflow-hidden rounded-xl transition-all duration-300 transform hover:scale-[1.05] active:scale-[0.95]"
      title={title}
      style={{
        background: isDarkMode
          ? 'linear-gradient(135deg, rgba(71,85,105,0.4) 0%, rgba(75,85,99,0.4) 50%, rgba(71,85,105,0.4) 100%)'
          : '#F1F5F9',
        backdropFilter: isDarkMode ? 'blur(16px)' : 'none',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid #E2E8F0',
        boxShadow: isDarkMode
          ? '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
          : 'none'
      }}
    >
      <div 
        className="absolute inset-0 rounded-xl transition-all duration-300 opacity-0 group-hover/action:opacity-100"
        style={{
          background: isDarkMode
            ? (isDelete
                ? 'linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.2) 50%, rgba(239,68,68,0.15) 100%)'
                : 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.2) 50%, rgba(59,130,246,0.15) 100%)')
            : (isDelete
                ? 'rgba(254,226,226,0.8)'
                : 'rgba(219,234,254,0.8)')
        }}
      ></div>
      
      <div className="relative p-2">
        {React.cloneElement(icon, {
          className: `w-4 h-4 transition-all duration-300 ${
            isDarkMode
              ? `text-white/70 group-hover/action:text-${isDelete ? 'red' : 'blue'}-300`
              : `text-slate-600 group-hover/action:text-${isDelete ? 'red' : 'blue'}-600`
          } group-hover/action:scale-110`
        })}
      </div>
      
      <div className="absolute inset-0 opacity-0 group-hover/action:opacity-100 transition-opacity duration-400">
        <div 
          className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/action:translate-x-full transition-transform duration-600"
          style={{
            background: isDarkMode
              ? (isDelete
                  ? 'linear-gradient(90deg, transparent 0%, rgba(239,68,68,0.2) 50%, transparent 100%)'
                  : 'linear-gradient(90deg, transparent 0%, rgba(59,130,246,0.2) 50%, transparent 100%)')
              : 'transparent'
          }}
        ></div>
      </div>
    </button>
  );

  const renderProductRow = (product) => (
    <div key={product.id} className="relative group/product">
      <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-gray-900/20 via-gray-800/15 to-gray-900/20 backdrop-blur-xl border border-white/20 shadow-[0_4px_16px_rgba(0,0,0,0.2)] group-hover/product:shadow-[0_8px_24px_rgba(0,0,0,0.3)]' : 'bg-white border border-slate-200 shadow-sm group-hover/product:shadow-md'} rounded-2xl transition-all duration-300`}></div>
      <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10 opacity-0 group-hover/product:opacity-100' : 'hidden'} rounded-2xl transition-opacity duration-300`}></div>

      <div className="relative p-4 grid grid-cols-6 gap-4 items-center">
        <div>
          <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{product.name}</div>
          <div className={`text-sm line-clamp-2 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`} title={product.description}>{product.description}</div>
          {product.sku && (
            <div className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>SKU: {product.sku}</div>
          )}
        </div>

        <div>
          <span className={`relative inline-flex items-center px-3 py-1 border text-xs font-medium rounded-full ${isDarkMode ? 'bg-blue-500/20 border-blue-400/30 text-blue-200 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]' : 'bg-brand/10 border-brand/20 text-brand'}`}>
            {product.category || t('common.noCategory')}
          </span>
        </div>

        <div>
          <div className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'} font-medium`}>
            {parseFloat(product.price).toFixed(2)} {product.currency}
          </div>
          <div className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>
            {t('common.per')} {product.unit || 'STK'}
          </div>
        </div>

        <div>
          <span className={`relative inline-flex items-center px-3 py-1 border text-xs font-medium rounded-full ${
            product.is_active
              ? (isDarkMode ? 'bg-emerald-500/20 border-emerald-400/30 text-emerald-200 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]' : 'bg-emerald-50 border-emerald-200 text-emerald-700')
              : (isDarkMode ? 'bg-red-500/20 border-red-400/30 text-red-200 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]' : 'bg-red-50 border-red-200 text-red-700')
          }`}>
            {product.is_active ? (
              <>
                <Eye className="w-3 h-3 mr-1" />
                {t('common.active')}
              </>
            ) : (
              <>
                <EyeOff className="w-3 h-3 mr-1" />
                {t('common.inactive')}
              </>
            )}
          </span>
        </div>
        
        <div className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-slate-700'} font-medium`}>
          {product.stock_quantity !== null ? product.stock_quantity : t('admin.products.unlimited')}
        </div>
        
        <div className="flex items-center justify-end space-x-2">
          {renderActionButton(
            () => onEditProduct(product),
            <Edit />,
            t('common.edit')
          )}
          {renderActionButton(
            () => onDeleteProduct(product.id, product.name),
            <Trash2 />,
            t('common.delete'),
            true
          )}
        </div>
      </div>
    </div>
  );

  const renderProductList = () => (
    <div className="relative group">
      <div className={styles.glassBackground}></div>
      <div className={styles.glassOverlay}></div>
      
      <div className="relative">
        {filteredProducts.length === 0 ? renderEmptyState() : (
          <div className="p-6 space-y-4">
            {renderProductTableHeader()}
            {filteredProducts.map(renderProductRow)}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {renderFilters()}
      {loading ? renderLoading() : renderProductList()}
    </>
  );
};

export default ProductList;